import React, { useEffect, useState } from "react";
import { TrendingUp, ClipboardList, Package, Star, Landmark } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import client from "@/lib/api";

const KPI = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-2xl p-3.5" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
    <div className="flex items-start justify-between mb-1.5">
      <span className="text-[11px] font-semibold" style={{ color: "var(--k-ink-soft)" }}>{label}</span>
      <Icon size={16} style={{ color: "var(--k-gold-deep)" }} />
    </div>
    <div className="font-num text-[20px] font-black">{value}</div>
    {sub && <div className="text-[10.5px] mt-0.5 font-bold" style={{ color: "var(--k-emerald)" }}>{sub}</div>}
  </div>
);

export default function MerchantOverview({ onSelect }) {
  const [d, setD] = useState(null);
  useEffect(() => { client.get("/merchant/overview").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="p-8 text-center" style={{ color: "var(--k-ink-soft)" }}>جارٍ التحميل…</div>;

  return (
    <div className="p-4" dir="rtl">
      <h1 className="font-head text-[20px] font-black">مرحباً بك، {d.store}</h1>
      <p className="text-[12.5px] mb-4" style={{ color: "var(--k-ink-soft)" }}>إدارة متجرك ومنتجاتك بسهولة</p>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-2.5 mb-4">
        <KPI icon={TrendingUp} label="إجمالي المبيعات" value={`${d.kpi.salesTotal} د.ل`} sub="مدفوعة" />
        <KPI icon={ClipboardList} label="عدد الطلبات" value={d.kpi.ordersCount} sub="كل الطلبات" />
        <KPI icon={Package} label="المنتجات النشطة" value={d.kpi.activeProducts} />
        <KPI icon={Star} label="تقييم البائع" value={d.rating} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 mb-4">
        <div className="xl:col-span-2 rounded-2xl p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          <h2 className="font-head text-[15px] font-bold mb-2">المبيعات خلال 7 أيام</h2>
          <div style={{ width: "100%", height: 190 }}>
            <ResponsiveContainer>
              <LineChart data={d.salesSeries.map((s) => ({ day: s.day.slice(5), value: s.value }))} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--k-line)" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "var(--k-ink-soft)" }} />
                <YAxis tick={{ fontSize: 10, fill: "var(--k-ink-soft)" }} width={48} />
                <Tooltip formatter={(v) => [`${v} د.ل`, "المبيعات"]} contentStyle={{ fontFamily: "Tajawal", fontSize: 12, direction: "rtl" }} />
                <Line type="monotone" dataKey="value" stroke="var(--k-gold)" strokeWidth={2.5} dot={{ r: 3, fill: "var(--k-gold-deep)" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-3">
          <div className="rounded-2xl p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-head text-[15px] font-bold">الطلبات الحديثة</h2>
              <button onClick={() => onSelect && onSelect("orders")} className="k-tap text-[11.5px] font-bold bg-transparent border-0" style={{ color: "var(--k-gold-deep)" }}>عرض الكل ›</button>
            </div>
            {d.recentOrders.length === 0 ? <p className="text-[12px]" style={{ color: "var(--k-ink-soft)" }}>لا توجد طلبات</p> : d.recentOrders.map((o, i) => (
              <div key={o.orderId} className="flex justify-between items-center py-1.5 text-[12px]" style={{ borderTop: i ? "1px solid var(--k-line)" : "none" }}>
                <span className="font-num font-bold">{o.orderId}</span>
                <span className="font-num" style={{ color: "var(--k-ink-soft)" }}>{o.total} د.ل</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--k-gold-tint)", color: "var(--k-gold-deep)" }}>{o.statusLabel}</span>
              </div>
            ))}
          </div>
          <div className="rounded-2xl p-4 text-center" style={{ background: "var(--k-ink)", color: "#fff" }}>
            <Landmark size={20} className="mx-auto mb-1" style={{ color: "var(--k-gold)" }} />
            <div className="text-[11px] text-white/70">ملخص مستحقاتك المالية</div>
            <div className="font-num text-[24px] font-black my-1">{d.kpi.payout} <span className="text-[13px]">د.ل</span></div>
            <button onClick={() => onSelect && onSelect("orders")} className="k-tap w-full py-2 rounded-lg text-[12px] font-bold" style={{ background: "var(--k-gold)", color: "var(--k-ink)" }}>تفاصيل المبيعات</button>
          </div>
        </div>
      </div>

      {d.topProducts.length > 0 && (
        <div className="rounded-2xl p-4 mb-2" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          <h2 className="font-head text-[15px] font-bold mb-2">منتجاتك الأكثر مبيعاً</h2>
          {d.topProducts.map((p, i) => (
            <div key={i} className="flex justify-between items-center py-1.5 text-[12.5px]" style={{ borderTop: i ? "1px solid var(--k-line)" : "none" }}>
              <span className="truncate" style={{ maxWidth: 200 }}>{p.name}</span>
              <span className="font-num font-bold" style={{ color: "var(--k-gold-deep)" }}>{p.qty} مبيع</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
